import React from "react";
import {SettingsDoc, User} from "../types/Types";
import {POINTS_CONFIG} from "./points/pointsConfig";

export const LIKE_COLOUR_LIKED = "#ff5c5ce3";
export const LIKE_COLOUR_UNLIKED = "rgba(255, 255, 255, 0.72)";

export function addAnchorTags (input: string): string  {
    const regex = /((?:https?|ftp):\/\/[\w\-_]+(?:\.[\w\-_]+)+[\w\-.,@?^=%&:/~+#]*[\w\-@?^=%&/~+#])\b/gi;
    // First convert URLs to anchor tags, then convert newlines to <br>
    return input.replace(regex, '<a href="$1">$1</a>').replace(/\n/g, '<br>');
}

export function sanitizeHtmlString(htmlString: string): string | null {
    // Simply remove any HTML tags while preserving newlines
    // This removes potential XSS while keeping the text formatting
    const withoutTags = htmlString.replace(/<[^>]*>/g, '');
    
    if(!withoutTags) {
        return "";
    }

    return withoutTags;
}

export function getDate(lastModified: Date): string {
    var now: any = lastModified;
    const date: Date = new Date(now.seconds * 1000);
    return date.toDateString();
}

function normaliseDate(value: any): Date {
    if (value instanceof Date) {
        return value;
    }

    if (value && typeof value.toDate === "function") {
        return value.toDate();
    }

    if (value && typeof value.seconds === "number") {
        return new Date(value.seconds * 1000);
    }

    if (typeof value === "number" || typeof value === "string") {
        return new Date(value);
    }

    return new Date(NaN);
}

export function getPhotoDate(lastModified: Date): string {
    const date = normaliseDate(lastModified);
    if (Number.isNaN(date.getTime())) {
        return "Unknown date";
    }

    const day = date.getDate();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}${addNumberSuffix(day)} ${month} ${year}`;
}

export function getTime(lastModified: Date): string {
    const date = normaliseDate(lastModified);
    if (Number.isNaN(date.getTime())) {
        return "Unknown time";
    }

    return date.toTimeString().substring(0, 5);
}

export function getMessageDate(messageSent: Date): string {
    const now: any = messageSent;
    const date: Date = new Date(now.seconds * 1000);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    //const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear().toString().slice(2);
    return `${hours}:${minutes} - ${day} ${month} ${year}`;
}

export function getDatePlusHours(now: Date, hours: number): Date {
    return new Date(now.getTime() + (hours * 60 * 60 * 1000));
}

export function formatMetres(metres: number): string {
    if (metres < 1) {
        return `${metres * 100} centimetres`;
    } else if (metres < 1000) {
        return `${metres.toFixed(0)} metres`;
    } else {
        return `${Number((metres / 1000).toFixed(0)).toLocaleString()} kilometres`;
    }
}

export function formatKilometres(kilometres: number): string {
    // Handle zero or very small values (< 1 meter)
    if (kilometres < 0.001) {
        return "0 m";
    } else if (kilometres < 1) {
        return `${Math.round(kilometres * 1000)} m`;
    } else if (kilometres < 10) {
        return `${(kilometres).toFixed(1)} km`;
    } else {
        return `${Number((kilometres).toFixed(0)).toLocaleString()} km`;
    }
}

export const LOCATION_LIMIT = 5000;
export const USERS_LIMIT = 10000;
export const NOTIFICATION_LIMIT = 100;
export const PHOTOS_LIMIT = 5000;

export function addNumberSuffix(number: number): string {
    const lastDigit = number % 10;
    const lastTwoDigits = number % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
        return "th";
    }

    switch (lastDigit) {
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
}

export function formatTime(hours: number): string {
    if (hours < 24) {
        return `${Math.floor(hours)}h`;
    }

    const days = Math.floor(hours / 24);
    const remainingHours = Math.floor(hours % 24);

    if (days < 7) {
        let result = `${days}d`;
        if (remainingHours > 0) {
            result += ` ${remainingHours}h`;
        }
        return result;
    }

    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;

    let result = `${weeks}w`;
    if (remainingDays > 0) {
        result += ` ${remainingDays}d`;
    }
    if (remainingHours > 0) {
        result += ` ${remainingHours}h`;
    }

    return result;
}

export function formatNumber(numberToFormat: number): string {
    return numberToFormat.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function formatNumberToDays(numberToFormat: number): string {
    return formatNumber(Math.floor(numberToFormat / 24))
}

export function getDateFromTimeStamp(date: any): Date {
    return new Date(date.seconds * 1000);
}

export function formatDateDDMMYYYY(dateValue: any): string {
    const date = dateValue?.seconds ? new Date(dateValue.seconds * 1000) : new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
}

export function truncateString(str: string): string {
    if (str.length > 21) {
        return str.substring(0, 21) + "...";
    } else if (str.length > 19) {
        return capitalizeFirstLetters(str);
    } else {
        return str;
    }
}

export const capitalizeFirstLetters = (text: string): string => {
    return text.replace(/\b\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

export function calculatePoints(totalSessions: number, uniqueSessions: number, bonusPoints: number | undefined) {
    return Math.floor(
        ((((totalSessions || 0) - (uniqueSessions || 0)) * POINTS_CONFIG.POINTS_PER_HOUR) + ((uniqueSessions || 0) * POINTS_CONFIG.FIRST_VISIT_POINTS)) + (bonusPoints || 0)
    );
}

export function calculatePointsV2(userName: string, totalSessions: number, accurateCount: number, uniqueSessions: number, bonusPoints: number | undefined) {
    return Math.floor(
        ((((accurateCount || 0) - (uniqueSessions || 0)) * POINTS_CONFIG.POINTS_PER_HOUR) + ((uniqueSessions || 0) * POINTS_CONFIG.FIRST_VISIT_POINTS)) + (bonusPoints || 0)
    );
}

export function formatPoints(num: number): string {
    if (num < 1000) {
        return num.toString();
    } else if (num < 10000) {
        return (Math.floor(num / 100) / 10).toFixed(1).replace('.0', '') + 'k';
    } else {
        return (Math.floor(num / 1000)) + 'k';
    }
}

export function calculateAndFormatPoints(user: User): string {
    return formatPoints(calculatePoints(user.sessionCount, user.sessionVerifiedCount, user.bonusPoints));
}

export const getGoogleMapsLink = (address: string): string => {
    // Replace spaces with plus signs in the address string
    const formattedAddress = address.replace(/ /g, "+");

    // Construct the Google Maps URL with the formatted address
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${formattedAddress}`;

    return googleMapsUrl;
};

export function isChatEnabled(user: User | undefined, settings: SettingsDoc | undefined): boolean {
    return Boolean((user && !user.socialDisabled && user.chatEnabled === undefined && settings && settings.chatEnabled) || (user && !user.socialDisabled && user.chatEnabled));
}

export function isPhotoEnabled(user: User | undefined, settings: SettingsDoc | undefined): boolean {
    return Boolean((user && user.photoEnabled === undefined && settings && settings.photoEnabled) || (user && user.photoEnabled));
}

export const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (crypto.getRandomValues(new Uint8Array(1))[0] % 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

export function resizeImage(fileBlob: Blob, width: number, height: number) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            const img = new Image();

            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d')!;

                // Draw the image on the canvas with the new size
                ctx.drawImage(img, 0, 0, width, height);

                // Convert the canvas to a Blob
                canvas.toBlob((blob) => {
                    resolve(blob);
                }, 'image/jpeg');
            };

            img.onerror = (error) => {
                reject(error);
            };

            img.src = event!.target!.result as string;
        };

        reader.onerror = (error) => {
            reject(error);
        };

        reader.readAsDataURL(fileBlob);
    });
}

export function generateTags(tags: string): Array<string> {
    if(!tags) {
        return []
    }
    return tags
        .split(',')
        .map(item => item.trim().toLowerCase())
        .filter(item => item && item !== 'null' && item !== 'undefined')
};

export function getDistanceFromLatLonInKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

export function formatTimeSince(timestamp: any): string {
    const date: Date = timestamp?.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 30) return `${diffDays} days ago`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
    const diffYears = Math.floor(diffDays / 365);
    return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}

export function formatTimeBetween(fromTimestamp: any, toTimestamp: any): string {
    const fromMs: number = fromTimestamp?.seconds ? fromTimestamp.seconds * 1000 : new Date(fromTimestamp).getTime();
    const toMs: number = toTimestamp?.seconds ? toTimestamp.seconds * 1000 : new Date(toTimestamp).getTime();
    const diffMs = Math.abs(toMs - fromMs);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 10) {
        const remainingMins = diffMins % 60;
        return remainingMins > 0 ? `${diffHours}h ${remainingMins}m` : `${diffHours}h`;
    }
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return '1 day';
    if (diffDays < 30) return `${diffDays} days`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return diffMonths === 1 ? '1 month' : `${diffMonths} months`;
    const diffYears = Math.floor(diffDays / 365);
    return diffYears === 1 ? '1 year' : `${diffYears} years`;
}

export function formatTimeBetweenDetailed(fromTimestamp: any, toTimestamp: any): string {
    const fromMs: number = fromTimestamp?.seconds ? fromTimestamp.seconds * 1000 : new Date(fromTimestamp).getTime();
    const toMs: number = toTimestamp?.seconds ? toTimestamp.seconds * 1000 : new Date(toTimestamp).getTime();
    const diffMs = Math.abs(toMs - fromMs);
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffSecs < 60) return `${diffSecs}s`;
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 10) {
        const remainingMins = diffMins % 60;
        return remainingMins > 0 ? `${diffHours}h ${remainingMins}m` : `${diffHours}h`;
    }
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays === 1) return '1 day';
    if (diffDays < 30) return `${diffDays} days`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return diffMonths === 1 ? '1 month' : `${diffMonths} months`;
    const diffYears = Math.floor(diffDays / 365);
    return diffYears === 1 ? '1 year' : `${diffYears} years`;
}

export function logDataLoaded(entityName: string, count: number): void {
    if (count > 50) {
        console.log(`Loaded ${entityName}: ${count} documents`);
    }
}

export function getCountryFlag(country: string): string | JSX.Element {
    const countryFlagMap: { [key: string]: string | JSX.Element } = {
        'England': '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
        'Scotland': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
        'Wales': '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
        'Northern Ireland': React.createElement(
            'svg',
            { width: '0.9em', height: '0.7em', viewBox: '0 0 20 15', style: {
                    display: 'inline-block', verticalAlign: 'middle', marginTop: '-7px', marginLeft: '2px' } },
            React.createElement('defs', {},
                React.createElement('pattern', { id: 'niflag', patternUnits: 'objectBoundingBox', width: '1', height: '1' },
                    React.createElement('image', { href: '/images/ni_flag_v2.png', x: '0', y: '0', width: '20', height: '15', preserveAspectRatio: 'none' })
                )
            ),
            React.createElement('path', {
                d: 'M0,2.5 Q5,3.5 10,2 T20,1.5 L20,13 Q15,12.5 10,13.5 T0,14 Z',
                fill: 'url(#niflag)'
            })
        ),
        'Ireland': '🇮🇪',
        'Isle of Man': '🇮🇲',
        'Spain': '🇪🇸',
        // Country codes
        'ENG': '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
        'SCO': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
        'WAL': '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
        'NIR': React.createElement(
            'svg',
            { width: '0.9em', height: '0.7em', viewBox: '0 0 20 15', style: {
                    display: 'inline-block', verticalAlign: 'middle', marginTop: '-7px', marginLeft: '2px' } },
            React.createElement('defs', {},
                React.createElement('pattern', { id: 'niflag2', patternUnits: 'objectBoundingBox', width: '1', height: '1' },
                    React.createElement('image', { href: '/images/ni_flag_v2.png', x: '0', y: '0', width: '20', height: '15', preserveAspectRatio: 'none' })
                )
            ),
            React.createElement('path', {
                d: 'M0,2.5 Q5,3.5 10,2 T20,1.5 L20,13 Q15,12.5 10,13.5 T0,14 Z',
                fill: 'url(#niflag2)'
            })
        ),
        'IRE': '🇮🇪',
        'IOM': '🇮🇲',
        'ESP': '🇪🇸'
    };
    
    return countryFlagMap[country] || '';
}

export function getCountryCode(country: string): string | null {
    const countryMap: { [key: string]: string } = {
        'England': 'ENG',
        'Northern Ireland': 'NIR',
        'Ireland': 'IRE',
        'Isle of Man': 'IOM',
        'Spain': 'ESP',
        'Wales': 'WAL',
        'Scotland': 'SCO'
    };
    
    return countryMap[country] || null;
}
